import { io, Socket } from "socket.io-client";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ReactNode,
  useState,
  useRef,
  useCallback,
  useContext,
  useEffect,
} from "react";

import { apiConfig } from "../../configs/api";
import { AuthenticationService } from "../../services/api/authentication";
import { ChatContext } from "./context";
import {
  ChatContextType,
  Message,
  AgentState,
  SQLQuery,
  DocSearch,
  ChartGenerated,
  ReasoningStep,
  ChatHistoryItem,
} from "./type";
import { LayoutContext } from "../layout/context";
import { Run } from "../../services/perspective_api/__generated__/perspective_server-types";
import { useCustomer } from "../app/provider";
import { VersionedDataContext } from "../versioned_data/context";

// Initial agent state
const initialAgentState: AgentState = {
  status: "idle",
  currentIteration: 0,
  maxIterations: 15,
  reasoning: "",
  reasoningHistory: [],
  sqlQueries: [],
  docSearches: [],
  charts: [],
};

const { Provider } = ChatContext;

const ServerURL = {
  socket: `${apiConfig.chatGateway.protocol}${apiConfig.chatGateway.host}`,
} as const;

const getRunInfo = (run: Run | null) =>
  run
    ? {
        input_id: run.runSettings.inputId,
        workflow_id: run.runSettings.workflowId,
        organization_id: run.runSettings.organizationId,
        settings_name_and_version: run.runSettings.settingsNameAndVersion,
        run_attempt_id: run.runSettings.runAttemptId,
        name: run.name,
        display_id: run.displayId,
      }
    : null;

export const ChatProvider = (props: { children: ReactNode }): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const socketRef = useRef<Socket | null>(null);
  const socketCustomerIdRef = useRef<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [chatContext, setChatContext] = useState<string | null>(null);
  const [agentState, setAgentState] = useState<AgentState>(initialAgentState);
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);

  // Refs to track current agent state for the done handler
  const agentStateRef = useRef<AgentState>(initialAgentState);
  const { editedRun, comparedRun, setEditedRun, setComparedRun } =
    useContext(VersionedDataContext);
  const { setIsComparing } = useContext(LayoutContext);

  const authToken = AuthenticationService.getIdToken();
  const { selectedCustomerId, whoIAm } = useCustomer();

  const sendMessage = useCallback(
    async (content: string) => {
      if (!chatContext) {
        console.error("No chat context available!");
        return;
      }

      if (!socketRef.current?.connected) {
        console.log("Socket not connected, attempting to reconnect...");
        await socketRef.current?.connect();
      }

      setIsAwaitingResponse(true);

      const message: Message = {
        type: "sent",
        content,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, message]);

      // Emit to Bedrock Agent chat handler
      socketRef.current?.emit(
        "ai_chat_message",
        JSON.stringify({
          message_content: content,
        })
      );
    },
    [editedRun, chatContext]
  );

  const connectChat = useCallback(
    (run: Run | null, context: string) => {
      console.log("Initializing socket connection");

      if (!run) {
        console.error("No run available!");
        return;
      }

      if (!authToken) {
        console.error("No auth token available!");
        return;
      }

      // Cleanup previous socket if it exists
      if (socketRef.current) {
        console.log("Cleaning up previous socket");
        socketRef.current.disconnect();
        socketRef.current.removeAllListeners();
      }

      // Reset all chat states before starting new session
      setIsConnected(false);
      setMessages([]);
      setIsAwaitingResponse(false);
      setChatSessionId(null);
      setChatContext(context);
      // Reset agent state to prevent stale data from previous session
      setAgentState(initialAgentState);
      agentStateRef.current = initialAgentState;
      socketCustomerIdRef.current = selectedCustomerId;

      // Create new socket with connection options
      const socket = io(ServerURL.socket, {
        auth: {
          cognito_id_token: authToken,
          customer_id: selectedCustomerId,
          source: "client-frontend",
          context,
          main_run_settings: JSON.stringify(getRunInfo(run)),
          user_email: whoIAm?.email || selectedCustomerId,
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
        transports: ["websocket", "polling"],
      });

      socketRef.current = socket;

      // Socket event handlers
      socket.on("connect", () => {
        console.log("Socket connected:", socket.id);

        if (socketRef.current && socketRef.current.id) {
          if (context === "forecast") {
            setEditedRun(run);
            setComparedRun(run);
            navigate({
              pathname: location.pathname.includes("_preview")
                ? location.pathname
                : location.pathname + "_preview",
              search: location.search,
            });
          }
          setIsConnected(true);
          setChatSessionId(socketRef.current.id);
        }
      });

      socket.on("disconnect", (reason) => {
        console.log("Socket disconnected:", reason);
        if (context === "forecast") {
          setEditedRun(null);
          setComparedRun(null);
          const newPath = location.pathname.replace("_preview", "");
          navigate({
            pathname: newPath,
          });
        }
        setIsConnected(false);
        setIsComparing(false);
        setChatSessionId(null);
        setChatContext(null);
      });

      socket.on("connect_error", (err) => {
        console.error("Connection error:", err.message);
        console.warn(ServerURL.socket);
        setIsConnected(false);
      });

      socket.on("reconnect", (attemptNumber) => {
        console.log("Socket reconnected after", attemptNumber, "attempts");
        setIsConnected(true);
      });

      socket.on("reconnect_attempt", (attemptNumber) => {
        console.log("Attempting to reconnect:", attemptNumber);
      });

      // ============================================
      // Streaming Event Listeners for Bedrock Agent
      // ============================================

      // Agent started processing
      socket.on("ai_start", (data) => {
        console.log("AI Start:", data);
        const newState: AgentState = {
          ...initialAgentState,
          status: "streaming",
        };
        setAgentState(newState);
        agentStateRef.current = newState;
      });

      // New iteration started
      socket.on("ai_iteration", (data) => {
        console.log(`AI Iteration: ${data.number}/${data.total}`);
        setAgentState((prev) => {
          const newState = {
            ...prev,
            currentIteration: data.number,
            maxIterations: data.total,
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Agent reasoning/thinking
      socket.on("ai_reasoning", (data) => {
        console.log("AI Reasoning:", data.text?.substring(0, 100) + "...");
        setAgentState((prev) => {
          const newStep: ReasoningStep = {
            text: data.text,
            iteration: data.iteration,
          };
          const newState = {
            ...prev,
            reasoning: data.text,
            reasoningHistory: [...prev.reasoningHistory, newStep],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // SQL query executed
      socket.on("ai_sql_executed", (data) => {
        console.log("AI SQL Executed:", data.sql?.substring(0, 100) + "...");
        console.log(`   Row count: ${data.row_count}`);
        setAgentState((prev) => {
          const newQuery: SQLQuery = {
            sql: data.sql,
            row_count: data.row_count,
            iteration: data.iteration,
            summary: data.summary,
            results: data.results,
            columns: data.columns,
          };
          const newState = {
            ...prev,
            sqlQueries: [...prev.sqlQueries, newQuery],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Documentation searched
      socket.on("ai_doc_searched", (data) => {
        console.log("[DEBUG] ai_doc_searched received:", {
          question: data.question,
          table_names: data.table_names,
          table_count: data.table_names?.length || 0,
          schema_info_length: data.schema_info?.length || 0,
          iteration: data.iteration,
        });
        setAgentState((prev) => {
          const newSearch: DocSearch = {
            question: data.question,
            schema_info: data.schema_info,
            table_names: data.table_names,
            iteration: data.iteration,
          };
          const newState = {
            ...prev,
            docSearches: [...prev.docSearches, newSearch],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Chart generated
      socket.on("ai_chart_generated", (data) => {
        console.log("AI Chart Generated:", data.chart_type, data.status);
        console.log("   Chart URL present:", !!data.chart_url);
        console.log("   Chart URL length:", data.chart_url?.length || 0);
        setAgentState((prev) => {
          const newChart: ChartGenerated = {
            chart_url: data.chart_url,
            chart_type: data.chart_type,
            status: data.status,
            message: data.message,
            iteration: data.iteration,
          };
          const newState = {
            ...prev,
            charts: [...prev.charts, newChart],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Final completion text
      socket.on("ai_completion", (data) => {
        console.log("AI Completion:", data.text?.substring(0, 100) + "...");
        // Completion is handled by the 'response' event
      });

      // Agent finished processing
      socket.on("ai_done", (data) => {
        console.log("AI Done:", data);
        setAgentState((prev) => {
          const newState = {
            ...prev,
            status: "done" as const,
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Agent error
      socket.on("ai_error", (data) => {
        console.error("AI Error:", data);
        setAgentState((prev) => {
          const newState = {
            ...prev,
            status: "error" as const,
            error: data.error,
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // ============================================
      // Chat History Response Handlers
      // ============================================

      socket.on("chat_history_response", (data) => {
        console.log("Chat history response:", data);
        setChatHistoryLoading(false);
        if (data.history) {
          setChatHistory(data.history);
        }
        if (data.error) {
          console.error("Chat history error:", data.error);
        }
      });

      // ============================================
      // Final Response Handler
      // ============================================

      socket.on("response", (data) => {
        setIsAwaitingResponse(false);

        console.log("Raw server response:", data);
        console.log("Response type:", typeof data);
        console.log("Response structure:", {
          hasType: data?.type,
          hasTitle: data?.title,
          hasTables: data?.tables,
          tablesLength: data?.tables?.length,
        });

        // Get the accumulated agent state from the ref
        const currentAgentState = agentStateRef.current;
        console.log("Agent state at response time:", {
          status: currentAgentState.status,
          sqlQueriesCount: currentAgentState.sqlQueries.length,
          chartsCount: currentAgentState.charts.length,
          charts: currentAgentState.charts,
        });

        let message: Message;

        if (data && typeof data === "object" && data.type === "table") {
          console.log("Creating table message with:", {
            title: data.title,
            tablesCount: data.tables?.length,
          });

          message = {
            type: "received",
            content: data.title || "",
            timestamp: new Date(),
            dataType: "table",
            tables: data.tables || [],
            sqlQueries: currentAgentState.sqlQueries.length > 0 ? currentAgentState.sqlQueries : undefined,
            charts: currentAgentState.charts.length > 0 ? currentAgentState.charts : undefined,
            // Persist full agent trace for "how did agent get here" display
            docSearches: currentAgentState.docSearches.length > 0 ? currentAgentState.docSearches : undefined,
            reasoningHistory: currentAgentState.reasoningHistory.length > 0 ? currentAgentState.reasoningHistory : undefined,
            iterationCount: currentAgentState.currentIteration,
          };

          console.log("Created table message:", message);
        } else {
          let content = "";
          if (typeof data === "string") {
            content = data;
          } else if (data && data.message) {
            content = data.message;
          } else {
            content = JSON.stringify(data, null, 2);
          }

          message = {
            type: "received",
            content,
            timestamp: new Date(),
            dataType: "text",
            sqlQueries: currentAgentState.sqlQueries.length > 0 ? currentAgentState.sqlQueries : undefined,
            charts: currentAgentState.charts.length > 0 ? currentAgentState.charts : undefined,
            // Persist full agent trace for "how did agent get here" display
            docSearches: currentAgentState.docSearches.length > 0 ? currentAgentState.docSearches : undefined,
            reasoningHistory: currentAgentState.reasoningHistory.length > 0 ? currentAgentState.reasoningHistory : undefined,
            iterationCount: currentAgentState.currentIteration,
          };

          console.log("Created text message:", message);
        }

        setMessages((prev) => [...prev, message]);

        // Reset agent state after response is complete
        setAgentState(initialAgentState);
        agentStateRef.current = initialAgentState;
      });
    },
    [
      authToken,
      selectedCustomerId,
      whoIAm,
      location.pathname,
      location.search,
      navigate,
      setComparedRun,
      setEditedRun,
      setIsComparing,
    ]
  );

  const disconnectChat = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current?.disconnect();
      setMessages([]);
    }
  }, [socketRef]);

  useEffect(() => {
    if (isConnected && socketCustomerIdRef.current && socketCustomerIdRef.current !== selectedCustomerId) {
      console.log(`Org changed from ${socketCustomerIdRef.current} to ${selectedCustomerId}, disconnecting chat`);
      disconnectChat();
    }
  }, [selectedCustomerId, isConnected, disconnectChat]);

  // Fetch recent chat history
  const fetchChatHistory = useCallback((limit: number = 10) => {
    if (!socketRef.current?.connected) {
      console.log("Socket not connected, cannot fetch chat history");
      return;
    }
    setChatHistoryLoading(true);
    socketRef.current.emit("get_chat_history", JSON.stringify({ limit }));
  }, []);

  // Load a conversation from history into the current chat
  const loadChatFromHistory = useCallback((historyItem: ChatHistoryItem) => {
    // Create messages from the history item
    const sentMessage: Message = {
      type: "sent",
      content: historyItem.query,
      timestamp: new Date(historyItem.timestamp),
    };

    const receivedMessage: Message = {
      type: "received",
      content: historyItem.response,
      timestamp: new Date(historyItem.timestamp),
      dataType: "text",
      sqlQueries: historyItem.sql_queries,
      docSearches: historyItem.doc_searches,
      charts: historyItem.charts,
      iterationCount: historyItem.iterations,
    };

    // Set messages (this replaces current messages)
    setMessages([sentMessage, receivedMessage]);
  }, []);

  useEffect(() => {
    if (comparedRun) {
      setIsComparing(true);
    } else {
      setIsComparing(false);
    }
  }, [setIsComparing, comparedRun]);

  // Debug: Log when agentState changes in Provider
  useEffect(() => {
    console.log("PROVIDER useEffect - agentState changed:", {
      status: agentState.status,
      iteration: agentState.currentIteration,
      chartsCount: agentState.charts.length,
    });
  }, [agentState]);

  const value: ChatContextType = {
    isConnected,
    messages,
    setMessages,
    sendMessage,
    connectChat,
    disconnectChat,
    isAwaitingResponse,
    chatSessionId,
    chatContext,
    agentState,
    // Chat history
    chatHistory,
    chatHistoryLoading,
    fetchChatHistory,
    loadChatFromHistory,
  };

  console.log("ChatProvider RENDER - agentState.status:", agentState.status);

  return <Provider value={value}>{props.children}</Provider>;
};
